/**
 * SuggestMifirModal — P10 (Pedro 2026-10-11): "suggest from NIF button, with a picker. It needs
 * to be restricted to a Party in the Agreement."
 *
 * Lists the agreement's parties (holders first) with the MiFIR identifier each would give:
 * an individual client's NCI ("PT" + NIF for a Portuguese national) or a company client's
 * LEI. A party with nothing to offer is shown greyed with the reason (§3h); a value that
 * fails its check digits is offered with a warning. "Use this" puts the value in the form —
 * pending until the page's Save (§3g Pattern A); nothing is written here.
 * Mirrors the mnemonic suggestion (wand button on the field → dialog → apply).
 */
import { Alert, Button, Group, Loader, Modal, Radio, Stack, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { agreementsApi } from '../api/agreements';
import { PARTY_ROLE_LABELS, type UUID } from '../api/types';

export function SuggestMifirModal({
  opened, agreementId, idType, onApply, onClose,
}: {
  opened: boolean;
  agreementId: UUID | null;      // null = a new agreement, not saved yet
  idType: 'NCI' | 'LEI';
  onApply: (value: string) => void;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ['agreements', agreementId, 'mifir-suggestions', idType],
    queryFn: () => agreementsApi.mifirSuggestions(agreementId as UUID, idType),
    enabled: opened && !!agreementId,
    staleTime: 0,
  });
  const [picked, setPicked] = useState<string | null>(null);
  const options = q.data?.options ?? [];
  const chosen = options.find((o) => o.person_id === picked && o.value);
  const what = idType === 'NCI' ? 'NCI' : 'LEI';

  function close() {
    setPicked(null);
    onClose();
  }

  return (
    <Modal opened={opened} onClose={close} centered size="lg"
      title={<Text fw={600}>Suggest the MIFIR {what} from a party</Text>}>
      <Stack>
        <Text size="sm" c="dimmed">
          {idType === 'NCI'
            ? 'For an individual client: "PT" followed by the NIF, for a Portuguese national. Pick the party whose identifier this agreement reports.'
            : 'For a company client: the company\'s LEI. Pick the party whose identifier this agreement reports.'}
          {' '}The value goes into the form; it is saved with the agreement.
        </Text>
        {!agreementId && (
          <Alert color="gray">Save the agreement and add its parties first.</Alert>
        )}
        {agreementId && q.isLoading && <Group justify="center" p="md"><Loader size="sm" /></Group>}
        {agreementId && q.isError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {q.error instanceof Error ? q.error.message : 'The suggestions could not be loaded.'}
          </Alert>
        )}
        {agreementId && q.data && options.length === 0 && (
          <Alert color="gray">This agreement has no parties yet.</Alert>
        )}
        {options.length > 0 && (
          <Radio.Group value={picked} onChange={setPicked}>
            <Stack gap="sm">
              {options.map((o) => (
                <Radio
                  key={o.person_id}
                  value={o.person_id}
                  disabled={!o.value}
                  label={(
                    <Stack gap={0}>
                      <Group gap="xs">
                        <Text size="sm" fw={500} c={o.value ? undefined : 'dimmed'}>{o.display_name}</Text>
                        <Text size="xs" c="dimmed">
                          {o.roles.map((r) => PARTY_ROLE_LABELS[r] ?? r).join(' · ')}
                        </Text>
                      </Group>
                      {o.value
                        ? <Text size="sm" ff="monospace">{o.value}</Text>
                        : <Text size="xs" c="dimmed">{o.reason}</Text>}
                      {o.warning && <Text size="xs" c="orange.8">{o.warning}</Text>}
                    </Stack>
                  )}
                />
              ))}
            </Stack>
          </Radio.Group>
        )}
        <Text size="xs" c="dimmed">
          Rule used: a Portuguese national's NCI is "PT" + NIF; a company is identified by its LEI.
          Other nationalities: type the NCI.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={close}>Cancel</Button>
          <Button disabled={!chosen} onClick={() => { if (chosen?.value) { onApply(chosen.value); close(); } }}>
            Use this
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
